import { useState, useEffect, useCallback } from "react";
import {
  Plus, X, Loader2, Trash2, Edit3, FileSpreadsheet, Download,
  Upload, Search, Lightbulb, Plug, Cog, Package, CheckSquare, Square, Wifi,
  ListTree, LayoutList, Users, Wand2, Check,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { fmt, opts } from "./constants";
import { ImportDesgloseModal } from "./ImportDesgloseModal";
import { MapeoMORubroModal } from "./MapeoMORubroModal";
import { useCatalogoPresupuesto, CatalogoScopeBanner, CatalogoSinGuardar } from "./catalogoScope";

const GRUPOS = [
  { key: "iluminacion",   label: "Iluminación",   color: "bg-amber-100 text-amber-700",  icon: Lightbulb },
  { key: "tomacorriente", label: "Tomacorriente", color: "bg-blue-100 text-blue-700",    icon: Plug },
  { key: "especial",      label: "Especial",      color: "bg-violet-100 text-violet-700", icon: Cog },
  // Fix AF (2026-09-04): "necesitamos crear otro grupo dentro del catalogo de bocas.
  // El nuevo se debe llamar SEÑAL DEBIL" (datos, TV, telefonía, alarma, CCTV).
  { key: "senal_debil",   label: "Señal Débil",   color: "bg-teal-100 text-teal-700",    icon: Wifi },
  { key: "otros",         label: "Otros",         color: "bg-gray-100 text-gray-600",    icon: Package },
];

const grupoMeta = (g) => GRUPOS.find(x => x.key === g) || GRUPOS.find(x => x.key === "otros");

export function BocasTab() {
  // Fix CC (2026-09-10): dentro de un presupuesto, su copia; en la Base Madre, la base.
  const { catApi, budgetId, sinGuardar } = useCatalogoPresupuesto();
  const [bocas, setBocas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterGrupo, setFilterGrupo] = useState("todos");
  const [editing, setEditing] = useState(null); // null | "new" | bocaObject
  const [importing, setImporting] = useState(false);
  const [showDesglose, setShowDesglose] = useState(false);
  const [showMapeoMO, setShowMapeoMO] = useState(false);
  const [rubrosById, setRubrosById] = useState({});
  // PDF REV3 #1 — Selección múltiple para eliminación masiva
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: rubros }] = await Promise.all([
        axios.get(`${catApi}/bocas-precios/bocas`, opts),
        axios.get(`${catApi}/bocas-precios/mano-obra`, opts).catch(() => ({ data: [] })),
      ]);
      setBocas(Array.isArray(data) ? data : []);
      const map = {};
      (Array.isArray(rubros) ? rubros : []).forEach(r => { map[r._id] = r; });
      setRubrosById(map);
    } catch { toast.error("Error al cargar bocas"); }
    finally { setLoading(false); }
  }, [catApi]);

  useEffect(() => { if (!sinGuardar) fetchAll(); }, [fetchAll, sinGuardar]);

  // Fix CC (2026-09-10): un presupuesto nuevo todavía no tiene copia: no se edita la base desde acá.
  if (sinGuardar) return <CatalogoSinGuardar que="Las bocas" />;

  const filtered = bocas.filter(b => {
    const matchSearch = !search ||
      (b.tipo_boca || "").toLowerCase().includes(search.toLowerCase()) ||
      (b.descripcion || "").toLowerCase().includes(search.toLowerCase());
    const matchGrupo = filterGrupo === "todos" || (b.grupo || "iluminacion") === filterGrupo;
    return matchSearch && matchGrupo;
  });

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar esta boca del catálogo?")) return;
    try {
      await axios.delete(`${catApi}/bocas-precios/bocas/${id}`, opts);
      toast.success("Boca eliminada");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  // PDF REV3 #1 — Helpers de selección múltiple
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const visibleIds = filtered.map((b) => b._id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) {
      visibleIds.forEach((id) => next.delete(id));
    } else {
      visibleIds.forEach((id) => next.add(id));
    }
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`¿Eliminar ${selected.size} boca${selected.size !== 1 ? "s" : ""} del catálogo?\nEsta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${catApi}/bocas-precios/bocas/bulk-delete`, { ids: Array.from(selected) }, opts);
      toast.success(`${data.deleted || 0} boca${(data.deleted || 0) !== 1 ? "s" : ""} eliminada${(data.deleted || 0) !== 1 ? "s" : ""}`);
      clearSelection();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar bocas");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/bocas-precios/bocas/template`, "_blank");
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-select
    if (!file) return;
    if (!confirm(`¿Importar "${file.name}"? Esto AGREGARÁ los precios al catálogo actual. Si querés reemplazar todo, hacé Ctrl+Click.`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bocas-precios/bocas/import-excel?replace=false`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} bocas importadas (${data.skipped} omitidas)`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar Excel");
    } finally { setImporting(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
      {budgetId && <CatalogoScopeBanner onRecopiado={fetchAll} />}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="text-sm font-bold text-[#1A4B7A] uppercase tracking-wide flex items-center gap-2">
              <Lightbulb size={14} /> Catálogo de BOCAS DE INST. ELÉCTRICA
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Precios por tipo de boca — usados como sugerencia en C.Unit MAT del presupuesto.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Fix CC (2026-09-10): importar planillas, el desglose y el mapeo asistido
                escriben la BASE MADRE: dentro de un presupuesto no se muestran. */}
            {!budgetId && (<>
            <button onClick={handleDownloadTemplate}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="bocas-download-template-btn">
              <Download size={13} /> Plantilla Excel
            </button>
            <label className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer">
              {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {importing ? "Importando..." : "Importar Excel"}
              <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" disabled={importing} data-testid="bocas-import-excel-input" />
            </label>
            <button onClick={() => setShowDesglose(true)}
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="bocas-import-desglose-btn">
              <ListTree size={13} /> Importar Desglose (BOCAS.xlsx)
            </button>
            <button onClick={() => setShowMapeoMO(true)}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="bocas-mapeo-mo-btn">
              <Wand2 size={13} /> Mapeo MO Asistido
            </button>
            </>)}
            <button onClick={() => setEditing("new")}
              className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              data-testid="bocas-new-btn">
              <Plus size={13} /> Nueva Boca
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar tipo de boca..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="bocas-search-input" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterGrupo("todos")}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${filterGrupo === "todos" ? "bg-[#2E6DA4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid="bocas-grupo-todos">Todos</button>
            {GRUPOS.map(g => (
              <button key={g.key} onClick={() => setFilterGrupo(g.key)}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 ${filterGrupo === g.key ? "bg-[#2E6DA4] text-white" : g.color}`}
                data-testid={`bocas-grupo-${g.key}`}>
                <g.icon size={11} /> {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* PDF REV3 #1 — Barra de acciones masivas (visible cuando hay selección) */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 mb-3 px-4 py-2 bg-red-50 border-2 border-red-200 rounded-xl" data-testid="bocas-bulk-action-bar">
            <CheckSquare size={14} className="text-red-600 flex-shrink-0" />
            <span className="text-xs font-bold text-red-700">
              {selected.size} boca{selected.size !== 1 ? "s" : ""} seleccionada{selected.size !== 1 ? "s" : ""}
            </span>
            <button onClick={clearSelection} className="text-xs font-medium text-red-700 hover:underline">
              Limpiar selección
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="ml-auto flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
              data-testid="bocas-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              {bulkDeleting ? "Eliminando..." : `Eliminar ${selected.size} ítem${selected.size !== 1 ? "s" : ""}`}
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-xs">
            <thead className="bg-[#EBF3FB] border-b border-blue-100">
              <tr>
                <th className="w-10 px-3 py-2.5 text-center">
                  <button
                    onClick={toggleAll}
                    className="text-[#1A4B7A] hover:text-[#2E6DA4] inline-flex items-center justify-center"
                    title={allSelected ? "Deseleccionar todos" : "Seleccionar todos los visibles"}
                    data-testid="bocas-select-all-btn"
                    aria-label="Seleccionar todos"
                  >
                    {allSelected ? <CheckSquare size={15} /> : someSelected ? <CheckSquare size={15} className="opacity-60" /> : <Square size={15} />}
                  </button>
                </th>
                <th className="text-left px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Grupo</th>
                <th className="text-left px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Tipo de Boca</th>
                <th className="text-left px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Descripción</th>
                <th className="text-center px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Composición</th>
                <th className="text-left px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Rubro MO</th>
                <th className="text-right px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Precio MAT</th>
                <th className="text-center px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide">Unidad</th>
                <th className="text-right px-3 py-2.5 font-bold text-[#1A4B7A] uppercase tracking-wide w-24">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 9 }).map((__, j) => <td key={j} className="px-3 py-2.5"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400">
                    <Lightbulb size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="font-medium">No hay bocas en el catálogo</p>
                    <p className="text-xs mt-1">Importá una planilla Excel o creá manualmente.</p>
                  </td>
                </tr>
              ) : (
                filtered.map(b => {
                  const meta = grupoMeta(b.grupo || "iluminacion");
                  const Icon = meta.icon;
                  const isSel = selected.has(b._id);
                  const nComp = Array.isArray(b.componentes) ? b.componentes.length : 0;
                  return (
                    <tr
                      key={b._id}
                      className={`border-b border-gray-50 hover:bg-gray-50/60 ${isSel ? "bg-blue-50/40" : ""}`}
                      data-testid={`boca-row-${b._id}`}
                    >
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => toggleOne(b._id)}
                          className="text-[#1A4B7A] hover:text-[#2E6DA4] inline-flex items-center justify-center"
                          data-testid={`boca-select-${b._id}`}
                          aria-label="Seleccionar"
                        >
                          {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.color}`}>
                          <Icon size={10} /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-gray-900">{b.tipo_boca}</td>
                      <td className="px-3 py-2.5 text-gray-500 max-w-md whitespace-pre-wrap break-words" title={b.descripcion}>{b.descripcion || "—"}</td>
                      <td className="px-3 py-2.5 text-center">
                        {nComp > 0 ? (
                          <button onClick={() => setEditing(b)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 hover:bg-violet-200"
                            data-testid={`boca-componentes-badge-${b._id}`}>
                            <ListTree size={10} /> {nComp} ítem{nComp !== 1 ? "s" : ""}
                          </button>
                        ) : (
                          <span className="text-gray-300 text-[10px]">manual</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {b.mo_rubro_id && rubrosById[b.mo_rubro_id] ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700" data-testid={`boca-rubro-mo-${b._id}`}>
                            <Users size={10} /> {rubrosById[b.mo_rubro_id].descripcion}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-400" data-testid={`boca-rubro-mo-${b._id}`}>
                            Sin mapear
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold tabular-nums text-blue-700">{fmt(b.precio_mat)}</td>
                      <td className="px-3 py-2.5 text-center text-gray-600">{b.unidad}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => setEditing(b)} className="p-1 rounded hover:bg-blue-50 text-blue-600 mr-1" data-testid={`boca-edit-${b._id}`}>
                          <Edit3 size={13} />
                        </button>
                        <button onClick={() => handleDelete(b._id)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`boca-delete-${b._id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
          <FileSpreadsheet size={12} /> {filtered.length} boca{filtered.length !== 1 ? "s" : ""} mostrada{filtered.length !== 1 ? "s" : ""} de {bocas.length} total{bocas.length !== 1 ? "es" : ""}.
        </div>
      </div>

      <BocaEditModal
        open={!!editing}
        boca={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchAll(); }}
      />
      <ImportDesgloseModal
        open={showDesglose}
        type="bocas"
        onClose={() => setShowDesglose(false)}
        onImported={() => { setShowDesglose(false); fetchAll(); }}
      />
      <MapeoMORubroModal
        open={showMapeoMO}
        onClose={() => setShowMapeoMO(false)}
        onSaved={() => { setShowMapeoMO(false); fetchAll(); }}
      />
    </div>
  );
}

// ─── Edit Modal ────────────────────────────────────────────────────────────
function BocaEditModal({ open, boca, onClose, onSaved }) {
  const { catApi } = useCatalogoPresupuesto(); // Fix CC (2026-09-10)
  const [form, setForm] = useState({
    tipo_boca: "", descripcion: "", grupo: "iluminacion", precio_mat: 0, precio_mo: 0, unidad: "un",
  });
  const [componentes, setComponentes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(boca ? {
        tipo_boca: boca.tipo_boca || "",
        descripcion: boca.descripcion || "",
        grupo: boca.grupo || "iluminacion",
        precio_mat: boca.precio_mat || 0,
        precio_mo: boca.precio_mo || 0,
        unidad: boca.unidad || "un",
      } : {
        tipo_boca: "", descripcion: "", grupo: "iluminacion", precio_mat: 0, precio_mo: 0, unidad: "un",
      });
      setComponentes(Array.isArray(boca?.componentes) ? boca.componentes.map(c => ({ ...c })) : []);
    }
  }, [open, boca]);

  if (!open) return null;

  const totalComponentes = componentes.reduce((acc, c) => {
    const cant = Number(c.cantidad) || 0;
    const punit = Number(c.precio_unit) || 0;
    return cant === 0 ? acc : acc + cant * punit;
  }, 0);

  const updateComp = (idx, field, value) => {
    setComponentes(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };
  const addComp = () => setComponentes(prev => [...prev, { descripcion: "", unidad: "UN", cantidad: 1, precio_unit: 0, marca: "" }]);
  const removeComp = (idx) => setComponentes(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.tipo_boca.trim()) { toast.error("El tipo de boca es obligatorio"); return; }
    const compsClean = componentes.filter(c => (c.descripcion || "").trim());
    if (compsClean.length !== componentes.length) { toast.error("Hay componentes sin descripción"); return; }
    setSaving(true);
    try {
      const payload = {
        tipo_boca: form.tipo_boca.trim(),
        descripcion: form.descripcion.trim(),
        grupo: form.grupo,
        precio_mat: Number(form.precio_mat) || 0,
        precio_mo: Number(form.precio_mo) || 0,
        unidad: form.unidad.trim() || "un",
        componentes: compsClean.length > 0 ? compsClean.map(c => ({
          descripcion: c.descripcion.trim(),
          unidad: c.unidad || "UN",
          cantidad: Number(c.cantidad) || 0,
          precio_unit: Number(c.precio_unit) || 0,
          marca: c.marca || "",
          material_code: c.material_code || "",
        })) : null,
      };
      if (boca?._id) {
        await axios.put(`${catApi}/bocas-precios/bocas/${boca._id}`, payload, opts);
      } else {
        await axios.post(`${catApi}/bocas-precios/bocas`, payload, opts);
      }
      toast.success(boca?._id ? "Boca actualizada" : "Boca creada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="boca-edit-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{boca ? "Editar Boca" : "Nueva Boca"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Tipo de Boca *</label>
            <input value={form.tipo_boca} onChange={e => setForm(f => ({ ...f, tipo_boca: e.target.value }))}
              placeholder="Ej: Boca Iluminación Interior"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="boca-tipo-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Descripción</label>
            <textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              rows={2} placeholder="Composición / detalle..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Grupo</label>
              <select value={form.grupo} onChange={e => setForm(f => ({ ...f, grupo: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="boca-grupo-select">
                {GRUPOS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Unidad</label>
              <input value={form.unidad} onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Precio MAT (Gs.) {componentes.length > 0 && <span className="text-violet-600">— calculado de la composición</span>}
              </label>
              <input type="number" min="0" disabled={componentes.length > 0}
                value={componentes.length > 0 ? Math.round(totalComponentes) : form.precio_mat}
                onChange={e => setForm(f => ({ ...f, precio_mat: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#2E6DA4]/30 disabled:bg-gray-50 disabled:text-violet-700 disabled:font-bold" />
            </div>
          </div>

          {/* Composición / sub-ítems */}
          <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/60">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                <ListTree size={13} /> Composición (sub-ítems)
              </h4>
              <button type="button" onClick={addComp}
                className="flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] hover:text-[#1A4B7A]"
                data-testid="boca-add-componente-btn">
                <Plus size={12} /> Agregar componente
              </button>
            </div>
            {componentes.length === 0 ? (
              <p className="text-[11px] text-gray-400 py-2">Sin composición — el Precio MAT se ingresa manualmente arriba.</p>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 px-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                  <span className="flex-1 min-w-0">Descripción</span>
                  <span className="w-14 text-center">Un.</span>
                  <span className="w-16 text-right">Cant.</span>
                  <span className="w-24 text-right">Precio u.</span>
                  <span className="w-9"></span>
                  <span className="w-20 text-right">Subtotal</span>
                  <span className="w-5"></span>
                </div>
                {componentes.map((c, idx) => {
                  const opcional = (Number(c.cantidad) || 0) === 0;
                  return (
                    <div key={idx} className="flex items-center gap-1.5 bg-white rounded-lg p-2 border border-gray-100" data-testid={`boca-componente-row-${idx}`}>
                      <input value={c.descripcion} onChange={e => updateComp(idx, "descripcion", e.target.value)}
                        placeholder="Descripción" className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1 text-[11px]" />
                      <input value={c.unidad} onChange={e => updateComp(idx, "unidad", e.target.value)}
                        placeholder="Un." className="w-14 border border-gray-200 rounded-lg px-1.5 py-1 text-[11px] text-center" />
                      <input type="number" value={c.cantidad} onChange={e => updateComp(idx, "cantidad", e.target.value)}
                        placeholder="Cant." className="w-16 border border-gray-200 rounded-lg px-1.5 py-1 text-[11px] text-right" />
                      <input type="number" value={c.precio_unit} onChange={e => updateComp(idx, "precio_unit", e.target.value)}
                        placeholder="Precio u." className="w-24 border border-gray-200 rounded-lg px-1.5 py-1 text-[11px] text-right" />
                      <span className={`w-9 text-center text-[9px] font-bold rounded-full px-1 py-0.5 ${opcional ? "bg-amber-100 text-amber-700" : "bg-transparent text-transparent"}`}>
                        {opcional ? "opc." : "—"}
                      </span>
                      <span className="w-20 text-right text-[11px] font-bold tabular-nums text-blue-700">{fmt(opcional ? 0 : (Number(c.cantidad) || 0) * (Number(c.precio_unit) || 0))}</span>
                      <button type="button" onClick={() => removeComp(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`boca-remove-componente-${idx}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  );
                })}
                <div className="flex justify-end pt-1 border-t border-gray-100 text-xs font-bold text-gray-700">
                  Total materiales: <span className="text-violet-700 ml-1">{fmt(totalComponentes)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="boca-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            {saving ? "Guardando..." : (boca ? "Guardar" : "Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}
